import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import SelectInput from '@components/atoms/ui-kit/select-input';
import Switch from '@components/atoms/ui-kit/switch';
import { useGetModels } from '@utils/api/models/use-get-models.ts';
import { useThemeContext } from '@utils/providers/theme-context.tsx';
import type { PlatformSpec } from '@utils/types/connections/platforms.ts';
import type { ConnectionDefaults } from '@utils/types/connections/connection.ts';

interface Props {
  platform: PlatformSpec;
  value: ConnectionDefaults;
  onChange: (next: ConnectionDefaults) => void;
}

type LanguageOption = { label: string; value: string };

export default function StepDefaults({ platform, value, onChange }: Props) {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();
  const { data: modelsResp } = useGetModels();

  const selectedCodes = useMemo(
    () => value.caption_languages ?? [],
    [value.caption_languages]
  );

  const options = useMemo<LanguageOption[]>(() => {
    const fromServer =
      modelsResp?.result?.map(l => ({
        label: `${l.name} (${l.key})`,
        value: l.key
      })) ?? [];
    const known = new Set(fromServer.map(o => o.value));
    const extras = selectedCodes
      .filter(code => !known.has(code))
      .map(code => ({ label: code, value: code }));
    return [...fromServer, ...extras];
  }, [modelsResp, selectedCodes]);

  const selectedOptions = useMemo(
    () => options.filter(o => selectedCodes.includes(o.value)),
    [options, selectedCodes]
  );

  const handleChange = (next: unknown) => {
    const list = Array.isArray(next) ? (next as LanguageOption[]) : [];
    const codes = list
      .map(o => o?.value)
      .filter((code): code is string => typeof code === 'string' && !!code);
    onChange({ ...value, caption_languages: codes });
  };

  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm" style={{ color: getColour('inactiveText') }}>
        {t('publishing-wizard-defaults-help')}
      </p>

      <SelectInput
        id="publishing-defaults-languages"
        label={t('publishing-wizard-defaults-languages')}
        placeholder={t('publishing-wizard-defaults-languages-placeholder')}
        helperText={t('publishing-wizard-defaults-languages-help')}
        options={options}
        value={selectedOptions}
        onChange={handleChange}
        isMulti
        isSearchable
        size="small"
        labelClass="text-sm font-medium"
      />

      {platform.id === 'youtube' && (
        <div
          className="flex items-start justify-between gap-4 rounded-xl border p-3"
          style={{
            borderColor: getColour('hairline'),
            background: getColour('secondLayer')
          }}
        >
          <div className="flex flex-col gap-0.5">
            <p className="text-sm font-medium">
              {t('publishing-wizard-defaults-youtube-draft-title')}
            </p>
            <p className="text-xs" style={{ color: getColour('inactiveText') }}>
              {t('publishing-wizard-defaults-youtube-draft-help')}
            </p>
          </div>
          <Switch
            ariaLabel={t('publishing-wizard-defaults-youtube-draft-title')}
            checked={value.youtube_tracks_as_draft ?? false}
            onChange={e =>
              onChange({
                ...value,
                youtube_tracks_as_draft: e.target.checked
              })
            }
          />
        </div>
      )}
    </div>
  );
}
